import { Box, TextField } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useEffect, useState } from "react";

const FlexibleTimeInput = ({
  value,
  onChange,
  placeholder = "hh:mm am",
  required = false,
  error = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  // Helper function to validate hours and minutes
  const isValidTime = (hours, minutes) => {
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  // Helper function to validate partial input
  const isValidPartialInput = (input) => {
    // For 4-digit input (e.g., 2500)
    if (/^\d{4}$/.test(input)) {
      const h = parseInt(input.slice(0, 2), 10);
      const m = parseInt(input.slice(2, 4), 10);
      return isValidTime(h, m);
    }

    // For time with colon (e.g., 22:79)
    if (/^\d{1,2}:\d{1,2}$/.test(input)) {
      const [h, m] = input.split(":").map(Number);
      return isValidTime(h, m);
    }

    // For partial time with colon (e.g., 22:7)
    if (/^\d{1,2}:\d$/.test(input)) {
      const [h, m] = input.split(":").map(Number);
      return isValidTime(h, m);
    }

    // For time with AM/PM (e.g., 25a)
    if (/^\d{1,2}[ap]$/.test(input)) {
      const h = parseInt(input.slice(0, -1), 10);
      return isValidTime(h, 0);
    }

    // For time with colon and AM/PM (e.g., 22:79p)
    if (/^\d{1,2}:\d{1,2}[ap]$/.test(input)) {
      const [h, m] = input.slice(0, -1).split(":").map(Number);
      return isValidTime(h, m);
    }

    return true; // Allow other partial inputs
  };

  const tryFormatTime = (input) => {
    const cleaned = input.toLowerCase().replace(/\s+/g, "");

    // Validate partial input first
    if (!isValidPartialInput(cleaned)) {
      return null;
    }

    // Handle 4-digit military time (e.g., 1300)
    if (/^\d{4}$/.test(cleaned)) {
      const h = parseInt(cleaned.slice(0, 2), 10);
      const m = parseInt(cleaned.slice(2, 4), 10);
      if (!isValidTime(h, m)) return null;
      const time = moment(`${h}:${m}`, "HH:mm");
      return time.isValid() ? time.format("hh:mm A") : null;
    }

    // Handle time with AM/PM without colon (e.g., 1p, 8a, 12p)
    const ampmNoColon = cleaned.match(/^(\d{1,2})([ap])$/);
    if (ampmNoColon) {
      const [, hRaw, ap] = ampmNoColon;
      const h = parseInt(hRaw, 10);
      if (!isValidTime(h, 0)) return null;
      const hPadded = h.toString().padStart(2, "0");
      const time = moment(`${hPadded}:00 ${ap}m`, "hh:mm A");
      return time.isValid() ? time.format("hh:mm A") : null;
    }

    // Handle time with colon and AM/PM (e.g., 4:3a, 4:35p)
    const ampmWithColon = cleaned.match(/^(\d{1,2}):(\d{1,2})([ap])$/);
    if (ampmWithColon) {
      const [, hRaw, mRaw, ap] = ampmWithColon;
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (!isValidTime(h, m)) return null;
      const hPadded = h.toString().padStart(2, "0");
      const mPadded = m.toString().padStart(2, "0");
      const time = moment(`${hPadded}:${mPadded} ${ap}m`, "hh:mm A");
      return time.isValid() ? time.format("hh:mm A") : null;
    }

    // Handle time with colon (e.g., 4:35, 4:3)
    const withColon = cleaned.match(/^(\d{1,2}):(\d{1,2})$/);
    if (withColon) {
      const [, hRaw, mRaw] = withColon;
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (!isValidTime(h, m)) return null;
      
      // For 12-hour format (1-12), wait for AM/PM indicator
      if (h >= 1 && h <= 12) {
        return null; // Don't format yet, wait for AM/PM
      }
      
      // For 24-hour format (13-23), format immediately
      const hPadded = h.toString().padStart(2, "0");
      const mPadded = m.toString().padStart(2, "0");
      const time = moment(`${hPadded}:${mPadded}`, "HH:mm");
      return time.isValid() ? time.format("hh:mm A") : null;
    }

    // Handle partial time with colon (e.g., 4:3)
    const partialWithColon = cleaned.match(/^(\d{1,2}):(\d)$/);
    if (partialWithColon) {
      const [, hRaw, mRaw] = partialWithColon;
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (!isValidTime(h, m)) return null;
      
      // For 12-hour format (1-12), wait for AM/PM indicator
      if (h >= 1 && h <= 12) {
        return null; // Don't format yet, wait for AM/PM
      }
      
      // For 24-hour format (13-23), format immediately
      const hPadded = h.toString().padStart(2, "0");
      const mPadded = m.toString().padStart(2, "0");
      const time = moment(`${hPadded}:${mPadded}`, "HH:mm");
      return time.isValid() ? time.format("hh:mm A") : null;
    }

    return null;
  };

  useEffect(() => {
    if (value) {
      const m = moment(value);
      if (m.isValid()) {
        setInputValue(m.format("hh:mm A"));
      }
    } else {
      setInputValue("");
    }
  }, [value]);

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const cleaned = raw.toLowerCase().replace(/\s+/g, "");

    if (!/^\d/.test(cleaned)) {
      return;
    }

    // Prevent typing any letters other than 'a' or 'p'
    if (/[^0-9:ap]/.test(cleaned)) {
      return;
    }

    // Prevent typing invalid hours (24-99)
    if (/^\d{2}$/.test(cleaned)) {
      const h = parseInt(cleaned, 10);
      if (h > 23) return;
    }

    // Prevent typing invalid hours and minutes in 4-digit format (e.g., 2360, 2590)
    if (/^\d{3,4}$/.test(cleaned)) {
      const h = parseInt(cleaned.slice(0, 2), 10);
      const m = parseInt(cleaned.slice(2), 10);
      if (h > 23) return;
      if (cleaned.length === 3 && parseInt(cleaned[2], 10) > 5) return;
      if (cleaned.length === 4 && m > 59) return;
    }

    // Prevent typing invalid minutes after colon
    if (/^\d{1,2}:\d{1,2}$/.test(cleaned)) {
      const [h, m] = cleaned.split(":").map(Number);
      if (h > 23 || m > 59) return;
    }

    // Prevent typing invalid minutes while typing
    if (/^\d{1,2}:\d$/.test(cleaned)) {
      const [h, m] = cleaned.split(":").map(Number);
      if (h > 23 || m > 5) return;
    }

    // Prevent typing invalid hours with AM/PM
    if (/^\d{1,2}[ap]$/.test(cleaned)) {
      const h = parseInt(cleaned.slice(0, -1), 10);
      if (h > 23) return;
    }

    // Prevent typing invalid hours with colon and AM/PM
    if (/^\d{1,2}:\d{1,2}[ap]$/.test(cleaned)) {
      const [h, m] = cleaned.slice(0, -1).split(":").map(Number);
      if (h > 23 || m > 59) return;
    }

    setInputValue(raw);

    if (raw.length < 2) return;

    // Don't return early for partial time with colon if it has AM/PM
    if (raw.match(/^\d{1,2}:\d$/) && !raw.match(/^\d{1,2}:\d[ap]$/)) {
      // Check if it's 12-hour format (1-12) - allow typing to continue
      const match = raw.match(/^(\d{1,2}):(\d)$/);
      if (match) {
        const h = parseInt(match[1], 10);
        if (h >= 1 && h <= 12) {
          return; // Allow typing to continue for 12-hour format
        }
      }
      return;
    }

    const formatted = tryFormatTime(raw);
    if (formatted) {
      setInputValue(formatted);
      const parsed = moment(formatted, "hh:mm A");
      if (parsed.isValid()) {
        onChange(parsed.toDate());
      }
    }
  };

  const handleTimePickerChange = (newValue) => {
    if (newValue && moment(newValue).isValid()) {
      const formatted = moment(newValue).format("hh:mm A");
      setInputValue(formatted);
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <TimePicker
          value={value}
          onChange={(newVal) => {
            if (moment(newVal).isValid()) {
              handleTimePickerChange(newVal);
            }
          }}
          renderInput={(params) => (
            <TextField
              inputRef={params.inputRef}
              value={inputValue}
              InputProps={{ ...params.InputProps }}
              onChange={handleInputChange}
              placeholder={placeholder}
              variant="standard"
              required={required}
              error={error}
              sx={{
                "& .MuiInput-root": {
                  fontSize: "16px",
                  color: "#000",
                  fontWeight: value ? 500 : 400,
                  minWidth: 100,
                  "&:before": {
                    borderBottomColor: (() => {
                      if (error) return "#f44336";
                      if (value) return "#1976d2";
                      return "#e0e0e0";
                    })(),
                    borderBottomWidth: 2,
                  },
                  "&:hover:before": {
                    borderBottomColor: error ? "#f44336" : "#1976d2",
                  },
                  "&:after": {
                    borderBottomColor: error ? "#f44336" : "#1976d2",
                  },
                },
                "& .MuiInput-input": {
                  padding: "4px 0",
                  textAlign: "center",
                  color: "#000",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default FlexibleTimeInput;
